import React, { useState } from 'react';

import { Card, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { forceReloadInvoicePrints } from '@hooks/API/UseInvoices';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import MediaDrawerPreviewOnly from '@components/MediaDrawer/MediaDrawerPreviewOnly';
import PdfPrintDrawer from '@components/PdfPrintDrawer/PdfPrintDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const DocumentItemThumbStyle = styled.div`
    svg {
        width: auto;
        height: 2.75rem;
    }
`;

const Wrapper = styled.div`
    padding: 0;
    background: white;
    margin: 0 1rem 1rem 1rem;

    display: flex;
    justify-content: flex-start;
    align-items: flex-start;

    .prints-sider {
        width: 300px;
        padding: 1rem 1.5rem;
        font-weight: 300;
        font-size: ${(props) => props.theme.FontSizeSm};
        text-transform: uppercase;
    }

    .prints-container {
        margin-top: -1px;
        flex: 1 1 0;
        width: 100%;
        min-height: 8rem;

        .ant-card-grid {
            padding: 0;
            .print-item {
                width: 100%;
                position: relative;
                .print-item-actions {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                }
                .print-item-content {
                    padding: 3.2rem 1rem 0 2rem;
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 1rem;
                    .print-item-icon {
                    }
                    .print-item-texts {
                        .print-item-title {
                            font-size: ${(props) => props.theme.FontSizeBase};
                            font-weight: 600;
                        }
                        .print-item-description {
                            font-size: ${(props) => props.theme.FontSizeSm};
                        }
                    }
                }
            }
        }
    }

    .modal-loading-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1000;
    }

    .modal-loading-container-content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: #fff;
        border-radius: 0.5rem;
        padding: 3rem 5rem;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const GridCard = styled(Card.Grid)`
    float: left; //added because ant removed it to use flex- to update later
    width: 25%;
    height: ${(props) => (props.vertical ? 'auto' : '8rem')};
    @media only screen and (max-width: 1600px) {
        width: 33.33%;
    }
    @media only screen and (max-width: 1200px) {
        width: 50%;
    }
    @media only screen and (max-width: 600px) {
        width: 100%;
    }
    width: ${(props) => props.vertical && '100% !important'};
`;

const ConfirmedLabel = styled.div`
    color: ${(props) => props.theme.DangerColor};
`;

const InvoicePrints = ({ invoicePrints, invoiceId }) => {
    const breakpoints = useNwBreakPoints();
    const [editPrintDrawer, setEditPrintDrawer] = useState(false);
    const [selectedPrint, setSelectedPrint] = useState();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [generatePdfLoading, setGeneratePdfLoading] = useState(false);

    const onDeletePrint = async (print) => {
        try {
            await Axios.delete(`/docVersions/${print.ID}`);
            forceReloadInvoicePrints(invoiceId);
        } catch (error) {
            notification.error({
                message: 'Error deleting print',
                description: error?.message || 'Unknown error',
            });
        }
    };

    const onOpenEditPrintDrawer = (print) => {
        setSelectedPrint(print);
        setEditPrintDrawer(true);
    };

    const onCloseEditPrintDrawer = async (print) => {
        setSelectedPrint(null);
        setEditPrintDrawer(false);
        forceReloadInvoicePrints(invoiceId);
    };

    const handlePrintPreview = async (document) => {
        setGeneratePdfLoading(true);
        try {
            const url = await Axios.get(`/docVersions/pdf/${document.DocGuid}`);
            const documentUrl = new URL(url);
            setGeneratePdfLoading(false);
            setPreviewUrl(documentUrl.href);
        } catch (error) {
            setGeneratePdfLoading(false);
            notification.error({
                message: 'Error',
                description: 'Error loading contract preview',
            });
        }
    };

    return (
        <Wrapper>
            <div className="prints-sider">PRINTED INVOICES</div>
            <div className="prints-container">
                {invoicePrints.map((print) => (
                    <GridCard key={`print-${print.ID}`}>
                        <div className="print-item">
                            <div className="print-item-actions">
                                <NwDropDownMenu
                                    actions={[
                                        {
                                            name: '',
                                            key: 'edit',
                                            pinned: true,
                                            icon: byPrefixAndName.fasl['pen'],
                                            action: () => onOpenEditPrintDrawer(print),
                                        },
                                        {
                                            name: '',
                                            type: 'delete',
                                            key: 'delete',
                                            pinned: true,
                                            icon: byPrefixAndName.fasl['trash-alt'],
                                            confirmationPlacement: 'left',
                                            action: () => onDeletePrint(print),
                                        },
                                    ]}
                                />
                            </div>
                            <div className="print-item-content">
                                <div className="print-item-icon">
                                    <DocumentItemThumbStyle
                                        className="document-image"
                                        onClick={() => handlePrintPreview(print)}
                                    >
                                        <NwIcon icon={byPrefixAndName.fasl['file-contract']} />
                                    </DocumentItemThumbStyle>
                                </div>
                                <div className="print-item-texts">
                                    <div className="print-item-title" onClick={() => handlePrintPreview(print)}>
                                        {print.Area}
                                    </div>
                                    <div className="print-item-description" onClick={() => handlePrintPreview(print)}>
                                        <CreatedBy object={print} short textonly />
                                        {print.Confirmed && <ConfirmedLabel>Confirmed</ConfirmedLabel>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </GridCard>
                ))}
                <br style={{ clear: 'both' }} />
            </div>
            {editPrintDrawer && (
                <NwDrawer
                    onClose={onCloseEditPrintDrawer}
                    padding="0px"
                    title="Edit Printed Invoice"
                    visible={true}
                    whiteBg={false}
                    width="80vw"
                    maxWidth={'1024px'}
                >
                    <PdfPrintDrawer docVersion={selectedPrint} saveButton onClose={onCloseEditPrintDrawer} />
                </NwDrawer>
            )}
            {previewUrl && <MediaDrawerPreviewOnly pdfPath={previewUrl} onCloseDrawer={() => setPreviewUrl(null)} />}
            {generatePdfLoading && (
                <div className="modal-loading-container">
                    <div className="modal-loading-container-content">
                        <Loading small text="Generating PDF" textBlack />
                    </div>
                </div>
            )}
        </Wrapper>
    );
};

export default InvoicePrints;
